import React, { useEffect, useRef } from 'react';
import { Box, useTheme } from '@ublend-npm/aulaui-core';
import { downloadConnectedEmailAttachment } from '@core/api/integrations/integrationsApi';

// 1x1 transparent GIF — shown while the real image is being fetched so that
// the browser does not display a broken-image icon.
const TRANSPARENT_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

type InlineAttachment = {
  id: string;
  messageId: string;
  contentId: string;
};

type OutlookHtmlBodyProps = {
  html: string;
  /** Fallback when body is plain text (not HTML). */
  isHtml?: boolean;
  /**
   * Inline attachments (pasted images) for this message/thread.
   * OutlookHtmlBody uses these to lazily resolve `cid:<contentId>` img
   * references by fetching a pre-signed download URL on demand.
   */
  inlineAttachments?: InlineAttachment[];
};

// ── In-Memory CID Download Cache ─────────────────────────────────────────────
// Caches download promises by clean contentId so that multiple cards in a thread
// (or re-opened cards) reuse the same S3 pre-signed URL with 0 extra API calls.
const cidUrlCache = new Map<string, Promise<string | undefined>>();

const normalizeCid = (str?: string | null) =>
  (str ?? '').replace(/^cid:/i, '').replace(/^<|>$/g, '').trim().toLowerCase();

const fetchCidUrlCached = (
  provider: 'microsoft',
  messageId: string,
  attachmentId: string,
  cleanCid: string
): Promise<string | undefined> => {
  const existing = cidUrlCache.get(cleanCid);
  if (existing) {
    return existing;
  }

  const promise = downloadConnectedEmailAttachment(
    provider,
    messageId,
    attachmentId
  )
    .then(({ data }) => data?.downloadUrl)
    .catch(() => undefined);

  cidUrlCache.set(cleanCid, promise);
  return promise;
};
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders an email body.
 *
 * HTML emails are displayed inside a sandboxed <iframe> — this prevents
 * any scripts or external resources from running in the parent page context
 * while still faithfully rendering the original email layout.
 *
 * Inline images pasted into the email (src="cid:...") are resolved lazily:
 * each CID is matched to its attachment metadata, a pre-signed download URL
 * is fetched from the backend (and cached across thread cards), displaying an
 * animated shimmer loading skeleton until the image finishes downloading.
 *
 * Plain-text emails are rendered as a <Typography> with white-space preserved.
 */
const OutlookHtmlBody = ({
  html,
  isHtml = true,
  inlineAttachments,
}: OutlookHtmlBodyProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const theme = useTheme();

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !isHtml) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    const textColor = theme.palette.mode === 'dark' ? '#E0E0E0' : '#212121';
    const linkColor = theme.palette.mode === 'dark' ? '#90CAF9' : '#1565C0';

    const shimmerBg =
      theme.palette.mode === 'dark'
        ? 'linear-gradient(90deg, #2D2735 25%, #3A3444 50%, #2D2735 75%)'
        : 'linear-gradient(90deg, #EAE8ED 25%, #F5F4F7 50%, #EAE8ED 75%)';

    // Inject the raw HTML and add responsive styles, new tab base, and shimmer skeleton CSS
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <base target="_blank" />
          <style>
            * {
              box-sizing: border-box;
              max-width: 100% !important;
            }
            [id$="divRplyFwdMsg"] * {
              display: none;
            }
            hr:has(+ [id$="divRplyFwdMsg"]) {
              display: none !important;
            }
            .elementToProof {
              color: ${theme.palette.text.secondary} !important;
            }
            [class$="x_elementToProof"] {
              display:none;
            }
            html, body {
              margin: 0;
              padding: 0;
              font-family: Roboto, -apple-system, BlinkMacSystemFont, Arial, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              word-break: break-word;
              overflow-x: hidden !important;
              width: 100% !important;
              max-width: 100% !important;
              color: ${textColor} !important;
            }
            table {
              width: 100% !important;
              max-width: 100% !important;
              table-layout: auto !important;
            }
            td, th {
              word-break: break-word;
            }
            img, video, iframe, embed, object {
              max-width: 100% !important;
              height: auto !important;
            }
            @keyframes cidShimmer {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
            img.cid-image-loading {
              min-width: 180px;
              min-height: 120px;
              border-radius: 8px;
              background: ${shimmerBg};
              background-size: 200% 100%;
              animation: cidShimmer 1.5s infinite;
              display: inline-block;
              vertical-align: middle;
            }
            pre, code {
              white-space: pre-wrap !important;
              word-break: break-word !important;
            }
            a {
              color: ${linkColor};
              word-break: break-word;
            }
          </style>
        </head>
        <body>${html}</body>
      </html>
    `);
    doc.close();

    // Ensure all links open in a new tab via parent window.open
    const handleLinkClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest('a');
      if (target && target.href && !target.href.startsWith('javascript:')) {
        e.preventDefault();
        window.open(target.href, '_blank', 'noopener,noreferrer');
      }
    };

    doc.addEventListener('click', handleLinkClick);

    // Auto-resize the iframe to fit its content height
    const resize = () => {
      if (iframe && doc.body) {
        const height = Math.max(
          doc.body.scrollHeight,
          doc.documentElement?.scrollHeight || 0,
          doc.body.offsetHeight || 0
        );
        iframe.style.height = `${height}px`;
      }
    };

    // Resize after paint so images/tables have loaded their dimensions
    requestAnimationFrame(resize);
    iframe.onload = resize;

    // Observe layout/DOM changes inside iframe
    const observer = new MutationObserver(resize);
    if (doc.body) {
      observer.observe(doc.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', resize);
        img.addEventListener('error', resize);
      }
    });

    // ── Lazy CID resolution & Skeleton UI ───────────────────────────────────
    if (inlineAttachments && inlineAttachments.length > 0) {
      const cidImages = Array.from(
        doc.querySelectorAll<HTMLImageElement>('img[src^="cid:"]')
      );

      cidImages.forEach((img) => {
        const rawCid = img.getAttribute('src');
        const cleanCid = normalizeCid(rawCid);
        if (!cleanCid) return;

        const match = inlineAttachments.find(
          (a) => normalizeCid(a.contentId) === cleanCid
        );
        if (!match) return;

        // Apply loading skeleton style immediately while image is being fetched
        img.src = TRANSPARENT_PLACEHOLDER;
        img.classList.add('cid-image-loading');

        fetchCidUrlCached('microsoft', match.messageId, match.id, cleanCid)
          .then((url) => {
            if (url) {
              const tempImg = new Image();
              tempImg.src = url;
              tempImg.onload = () => {
                img.src = url;
                img.classList.remove('cid-image-loading');
                resize();
              };
              tempImg.onerror = () => {
                img.classList.remove('cid-image-loading');
              };
            } else {
              img.classList.remove('cid-image-loading');
            }
          })
          .catch(() => {
            img.classList.remove('cid-image-loading');
          });
      });
    }
    // ────────────────────────────────────────────────────────────────────────

    return () => {
      iframe.onload = null;
      doc.removeEventListener('click', handleLinkClick);
      observer.disconnect();
    };
  }, [html, isHtml, theme.palette.mode, inlineAttachments]);

  if (!isHtml) {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          fontFamily: 'Roboto, -apple-system, Arial, sans-serif',
          fontSize: '14px',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'hidden',
          maxWidth: '100%',
          color: 'text.secondary',
        }}
      >
        {html}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', maxWidth: '100%' }}>
      <iframe
        ref={iframeRef}
        title="Email content"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        style={{
          width: '100%',
          maxWidth: '100%',
          border: 'none',
          display: 'block',
          minHeight: 80,
          overflowX: 'hidden',
        }}
      />
    </Box>
  );
};

export default OutlookHtmlBody;
